import { useEffect, useRef, useState, type CSSProperties } from "react";
import { createPortal } from "react-dom";
import { NAV, SITE, SOCIAL } from "@/config/site";
import { useFocusTrap, useLockBody } from "@/hooks/useDialog";
import { cn } from "@/lib/cn";
import { Link } from "@/router/Router";
import { useAuth } from "@/state/AuthProvider";
import { useIsLive } from "@/state/LiveProvider";
import { useMessaging } from "@/state/MessagingProvider";
import { Button } from "@/components/ui/Button";
import { Icon, type IconName } from "@/components/ui/Icon";
import { Logo } from "@/components/ui/Logo";
import { ThemeToggle } from "@/components/ui/ThemeToggle";

/** Full-screen navigation for phones and tablets: large serif items, accordions, staggered entry. */
export function MobileMenu({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [mounted, setMounted] = useState(open);
  const [visible, setVisible] = useState(false);
  const [expanded, setExpanded] = useState<string | null>(null);
  const panel = useRef<HTMLDivElement>(null);
  const isLive = useIsLive();
  const { user } = useAuth();
  const { unreadTotal, enabled } = useMessaging();

  useEffect(() => {
    if (open) {
      setMounted(true);
      const r = requestAnimationFrame(() => requestAnimationFrame(() => setVisible(true)));
      return () => cancelAnimationFrame(r);
    }
    setVisible(false);
    const t = window.setTimeout(() => setMounted(false), 420);
    return () => window.clearTimeout(t);
  }, [open]);

  useLockBody(mounted);
  useFocusTrap(panel, open, onClose);
  if (!mounted) return null;

  return createPortal(
    <div id="mobile-menu" ref={panel} className={cn("mobile-menu", visible && "is-open")} role="dialog" aria-modal="true" aria-label="Menu">
      <div className="mobile-menu__top">
        <Link to={user ? "/welcome" : "/"} className="site-header__brand" onClick={onClose} aria-label="Miracle Assembly — home">
          <Logo />
        </Link>
        <button type="button" className="mobile-menu__close" onClick={onClose}>
          <span>Close</span>
          <Icon name="close" size={22} />
        </button>
      </div>

      <nav className="mobile-menu__nav" aria-label="Main">
        <ul>
          {NAV.map((item, i) => {
            const isOpen = expanded === item.label;
            return (
              <li key={item.label} className="mobile-menu__item" style={{ "--i": i } as CSSProperties}>
                {item.children ? (
                  <>
                    <button type="button" className="mobile-menu__link" aria-expanded={isOpen} onClick={() => setExpanded(isOpen ? null : item.label)}>
                      <span className="mobile-menu__num">0{i + 1}</span>
                      <span className="mobile-menu__label">{item.label}</span>
                      <Icon name="chevronDown" size={22} className="mobile-menu__chev" />
                    </button>
                    <div className={cn("mobile-menu__sub", isOpen && "is-open")}>
                      <ul>
                        {item.children.map((child) => (
                          <li key={child.href + child.label}>
                            <Link to={child.href} onClick={onClose} tabIndex={isOpen ? 0 : -1}>
                              {child.label}
                            </Link>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </>
                ) : (
                  <Link to={item.href} className="mobile-menu__link" onClick={onClose}>
                    <span className="mobile-menu__num">0{i + 1}</span>
                    <span className="mobile-menu__label">{item.label}</span>
                    <Icon name="arrowRight" size={22} className="mobile-menu__chev" />
                  </Link>
                )}
              </li>
            );
          })}
        </ul>
      </nav>

      <div className="mobile-menu__actions" style={{ "--i": NAV.length } as CSSProperties}>
        <Button to="/account" variant="flame" size="lg" icon="user" onClick={onClose} block>
          {user ? "Member dashboard" : "Sign in"}
        </Button>
        <Button to="/watch/live" variant={isLive ? "leaf" : "ghost"} size="lg" icon="radio" onClick={onClose} block>
          {isLive ? "We're live — watch" : "Watch live"}
        </Button>
      </div>

      <div className="mobile-menu__meta" style={{ "--i": NAV.length + 1 } as CSSProperties}>
        <Link to="/locations" onClick={onClose} className="mobile-menu__meta-link">
          <Icon name="pin" size={18} /> Locations & service times
        </Link>
        <Link to="/contact" onClick={onClose} className="mobile-menu__meta-link">
          <Icon name="mail" size={18} /> Contact · {SITE.email}
        </Link>
        {user && enabled ? (
          <Link to="/messages" onClick={onClose} className="mobile-menu__meta-link">
            <Icon name="message" size={18} /> Messages
            {unreadTotal > 0 ? <span className="count-pill">{unreadTotal > 99 ? "99+" : unreadTotal}<span className="sr-only"> unread</span></span> : null}
          </Link>
        ) : null}
        {user?.role === "admin" ? (
          <Link to="/admin/birthdays" onClick={onClose} className="mobile-menu__meta-link">
            <Icon name="cake" size={18} /> Birthdays
          </Link>
        ) : null}
        <Link to="/account" onClick={onClose} className="mobile-menu__meta-link">
          <Icon name="user" size={18} /> {user ? "Your account" : "Member sign in"}
        </Link>
        <ThemeToggle withLabel className="mobile-menu__theme" />
        <div className="social-row">
          {SOCIAL.map((s) => (
            <a key={s.label} href={s.href} target="_blank" rel="noopener noreferrer" aria-label={s.label} className="social-row__link">
              <Icon name={s.icon as IconName} size={18} />
            </a>
          ))}
        </div>
      </div>
    </div>,
    document.body,
  );
}
